import React from 'react';
import classnames from 'classnames';
import WordpressConverter from '@gik/blog/utils/WordpressConverter';
import getPreviewMode from '@gik/core/utils/PreviewMode';
import bemBlock from '@gik/core/utils/bemBlock';
import { FrameBone, ParagraphBone } from '@gik/ui/SkeletonLoader';
import withSuspense from '@gik/core/components/suspended/withSuspense';
import SvgIcon from '@gik/ui/SvgIcon';
import { RelationFilter } from '@gik/blog/api/wp/PostsUrlBuilder';
import { usePostsRelated } from '@gik/blog/api/wp';
import { useInkind } from '@gik/api/inkinds/inkind';
import { ArticleTile } from '@gik/ui';

const numItems = 3;

interface IRelatedArticlesProps extends React.HTMLAttributes<HTMLDivElement> {
  skeletonItems: number;
  relationFilters?: RelationFilter<string[]>[];
  inkindRouteId: string;
}

const skelBem = bemBlock(`related-articles-rows-skeleton`);
const Skeleton = ({ skeletonItems = numItems }: Partial<IRelatedArticlesProps>): React.ReactElement => (
  <div className={`${skelBem()}`}>
    {Array.from(Array(skeletonItems).keys()).map(index => {
      return (
        <div key={index} className={skelBem('item')}>
          <FrameBone className={skelBem('image')} />
          <main>
            <ParagraphBone words={20} className={skelBem('text')} />
            <div className={skelBem('toolbar')}>
              <SvgIcon name={'HeartOutline'} />
              <span className="separator">•</span>
              <ParagraphBone words={2} className={skelBem('author')} />
            </div>
          </main>
        </div>
      );
    })}
  </div>
);

function RelatedArticles({ relationFilters, inkindRouteId, className }: IRelatedArticlesProps): React.ReactElement {
  // either inkindRouteId or relationFilters must be defined
  const { data: inkindPage, error: inkindPageError } = useInkind(inkindRouteId);

  const situationSlugs = inkindRouteId && inkindPage?.inkindSituations.map(s => s.slug);

  const _relationFilters: RelationFilter<string[]>[] = inkindRouteId
    ? [
        {
          taxonomy: 'situation',
          terms: situationSlugs,
        },
        {
          taxonomy: 'article-type',
          terms: ['being-supportive'],
        },
      ]
    : relationFilters;

  const { data: relatedPostsData, error: relatedPostsDataError } = usePostsRelated(_relationFilters, numItems);

  if (!relatedPostsData) return <Skeleton />;

  const articles = relatedPostsData
    ? relatedPostsData
        .map(post => {
          return WordpressConverter.convertPost(post);
        })
        .filter(post => !post.isPreviewOnly || post.isPreviewOnly === getPreviewMode())
    : null;

  const bem = bemBlock('related-articles-rows');
  const blockClasses = classnames(bem(), className || '');

  const isLoading = !inkindPage || !relatedPostsData;
  const isError = isLoading && (inkindPageError || relatedPostsDataError);

  if (isLoading) return <Skeleton />;
  if (isError) return <Skeleton />;

  return (
    <div className={blockClasses}>
      {articles?.map(item => {
        if (!item) return null;
        return <ArticleTile article={item} key={item.articleId} row />;
      })}
    </div>
  );
}

export default withSuspense(RelatedArticles, Skeleton);
